import { useContext, useEffect, useMemo, useRef } from "react";
import {
  useDraggable,
  useDroppable,
  UseDraggableArguments,
  UseDroppableArguments,
} from "utils/core/src";
import type { Data } from "@dnd-kit/core";
import { CSS, isKeyboardEvent, useCombinedRefs } from "@dnd-kit/utilities";

import { Context } from "../components";
import type { Disabled, SortableData, SortingStrategy } from "../types";
import { isValidIndex } from "../utilities";
import {
  defaultAnimateLayoutChanges,
  defaultAttributes,
  defaultNewIndexGetter,
  defaultTransition,
  disabledTransition,
  transitionProperty,
} from "./defaults";
import type {
  AnimateLayoutChanges,
  NewIndexGetter,
  SortableTransition,
} from "./types";
import { useDerivedTransform } from "./utilities";

export interface Arguments
  extends Omit<UseDraggableArguments, "disabled">,
    Pick<UseDroppableArguments, "resizeObserverConfig"> {
  animateLayoutChanges?: AnimateLayoutChanges;
  disabled?: boolean | Disabled;
  getNewIndex?: NewIndexGetter;
  strategy?: SortingStrategy;
  transition?: SortableTransition | null;
}

export function useSortable({
  animateLayoutChanges = defaultAnimateLayoutChanges,
  attributes: userDefinedAttributes,
  disabled: localDisabled,
  data: customData,
  getNewIndex = defaultNewIndexGetter,
  id,
  strategy: localStrategy,
  resizeObserverConfig,
  transition = defaultTransition,
}: Arguments) {
  const {
    items,
    rows,
    containerId,
    activeIndex,
    disabled: globalDisabled,
    disableTransforms,
    sortedRects,
    overIndex,
    useDragOverlay,
    strategy: globalStrategy,
    direction,
    setDirection,
    overPercentage,
    setOverPercentage,
    nextRow,
    setNextRow,
    disableSort,
    setDisableSort,
  } = useContext(Context);

  /* console.log(
    "activeIndex",
    activeIndex,
    "sortedRects",
    sortedRects,
    "overIndex",
    overIndex
  ); */

  // console.log("activeIndex", activeIndex, "overIndex", overIndex);
  // console.log(hoveringRow);

  const disabled: Disabled = normalizeLocalDisabled(
    localDisabled,
    globalDisabled
  );
  const index = items.indexOf(id);
  const data = useMemo<SortableData & Data>(
    () => ({ sortable: { containerId, index, items }, ...customData }),
    [containerId, customData, index, items]
  );
  const itemsAfterCurrentSortable = useMemo(
    () => items.slice(items.indexOf(id)),
    [items, id]
  );
  const {
    rect,
    node,
    isOver,
    setNodeRef: setDroppableNodeRef,
  } = useDroppable({
    id,
    data,
    disabled: disabled.droppable,
    resizeObserverConfig: {
      updateMeasurementsFor: itemsAfterCurrentSortable,
      ...resizeObserverConfig,
    },
  });
  const {
    active,
    activatorEvent,
    activeNodeRect,
    attributes,
    setNodeRef: setDraggableNodeRef,
    listeners,
    isDragging,
    over,
    setActivatorNodeRef,
    transform,
  } = useDraggable({
    id,
    data,
    attributes: {
      ...defaultAttributes,
      ...userDefinedAttributes,
    },
    disabled: disabled.draggable,
  });

  const activeRow = active?.data.current;
  const overRow = over?.data.current;

  const setNodeRef = useCombinedRefs(setDroppableNodeRef, setDraggableNodeRef);
  const isSorting = Boolean(active);

  let isGroupHoveringGroup = false;

  if (!isDragging && activeRow?.isGroup) {
    if (overRow?.group_id || overRow?.isGroup) isGroupHoveringGroup = true;
  }

  let overCollapsedGroupId =
    activeRow?.isHabit &&
    nextRow?.isGroup &&
    !nextRow?.expanded &&
    overPercentage < 95 &&
    overPercentage > 10 &&
    nextRow.id;

  const displaceItem =
    isSorting &&
    !disableTransforms &&
    isValidIndex(activeIndex) &&
    isValidIndex(overIndex);

  const dragSourceDisplacement = displaceItem ? transform : null;
  const strategy = localStrategy ?? globalStrategy;
  // console.log(activeNodeRect);
  const finalTransform = displaceItem
    ? dragSourceDisplacement ??
      strategy({
        rects: sortedRects,
        activeNodeRect,
        activeIndex,
        overIndex,
        index,
      })
    : null;

  let newIndex =
    isValidIndex(activeIndex) && isValidIndex(overIndex)
      ? getNewIndex({ id, items, activeIndex, overIndex })
      : index;

  if (isGroupHoveringGroup || overCollapsedGroupId) {
    newIndex = index;
    setDisableSort(true);
  } else {
    setDisableSort(false);
  }

  // console.log("newIndex", newIndex);
  const activeId = active?.id;
  const previous = useRef({
    activeId,
    items,
    newIndex,
    containerId,
  });

  const previousY = useRef(transform?.y ?? 0);

  if (isDragging) {
    const currentY = transform?.y ?? 0;
    if (previousY.current > currentY) {
      setDirection("up");
    }
    if (previousY.current < currentY) {
      setDirection("down");
    }
    setOverPercentage(Math.round(Math.abs((currentY / 40) * 100)));
    previousY.current = transform?.y ?? 0;

    if (direction === "up") {
      setNextRow(rows[index - 1]);
    } else setNextRow(rows[index + 1]);
  }

  const itemsHaveChanged = items !== previous.current.items;
  const shouldAnimateLayoutChanges = animateLayoutChanges({
    active,
    containerId,
    isDragging,
    isSorting,
    id,
    index,
    items,
    newIndex: previous.current.newIndex,
    previousItems: previous.current.items,
    previousContainerId: previous.current.containerId,
    transition,
    wasDragging: previous.current.activeId != null,
  });

  const derivedTransform = useDerivedTransform({
    disabled: !shouldAnimateLayoutChanges,
    index,
    node,
    rect,
  });

  // if (id === 1) console.log(derivedTransform, finalTransform);

  useEffect(() => {
    if (isSorting && previous.current.newIndex !== newIndex) {
      previous.current.newIndex = newIndex;
    }

    if (containerId !== previous.current.containerId) {
      previous.current.containerId = containerId;
    }

    if (items !== previous.current.items) {
      previous.current.items = items;
    }
  }, [isSorting, newIndex, containerId, items]);

  useEffect(() => {
    if (activeId === previous.current.activeId) {
      return;
    }

    if (activeId && !previous.current.activeId) {
      previous.current.activeId = activeId;
      return;
    }

    const timeoutId = setTimeout(() => {
      previous.current.activeId = activeId;
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [activeId]);

  return {
    active,
    activeIndex,
    attributes,
    data,
    rect,
    index,
    newIndex,
    items,
    isOver,
    isSorting,
    isDragging,
    isDraggingHabit: active?.data.current?.isHabit,
    listeners,
    node,
    overIndex,
    over,
    overPercentage,
    overCollapsedGroupId,
    nextRow,
    setNodeRef,
    setActivatorNodeRef,
    setDroppableNodeRef,
    setDraggableNodeRef,
    transform: derivedTransform ?? finalTransform,
    transition: getTransition(),
  };

  function getTransition() {
    if (
      // Temporarily disable transitions for a single frame to set up derived transforms
      derivedTransform ||
      // Or to prevent items jumping to back to their "new" position when items change
      (itemsHaveChanged && previous.current.newIndex === index)
    ) {
      return disabledTransition;
    }

    if (!isKeyboardEvent(activatorEvent) || !transition) {
      return undefined;
    }

    if (isSorting || shouldAnimateLayoutChanges) {
      return CSS.Transition.toString({
        ...transition,
        property: transitionProperty,
      });
    }

    return undefined;
  }
}

function normalizeLocalDisabled(
  localDisabled: Arguments["disabled"],
  globalDisabled: Disabled
) {
  if (typeof localDisabled === "boolean") {
    return {
      draggable: localDisabled,
      // Backwards compatibility
      droppable: false,
    };
  }

  return {
    draggable: localDisabled?.draggable ?? globalDisabled.draggable,
    droppable: localDisabled?.droppable ?? globalDisabled.droppable,
  };
}
